import { useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { EInvoiceBuyerFields } from './einvoice-buyer-fields'
import { EInvoiceDraftLines, editableDraftItem } from './einvoice-draft-lines'
import { EInvoiceDraftReset } from './einvoice-draft-reset'
import {
    EInvoiceDraftTotals,
    draftTotalDiffers,
    draftTotals,
} from './einvoice-draft-totals'
import type {
    EInvoiceDraftContent,
    RefreshEInvoiceDraft,
} from '@/lib/api/einvoice-api'
import type { EInvoiceDraftValues } from '@/lib/schemas/einvoice-draft-schemas'
import { createEInvoiceDraftSchema } from '@/lib/schemas/einvoice-draft-schemas'
import { useEInvoiceDraftEditor } from '@/hooks/use-einvoice-query'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { todayYMD } from '@/lib/date-presets'
import { ApiError } from '@/lib/api'
import { resolveApiErrorMessage } from '@/lib/api-error-messages'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { m } from '@/lib/paraglide/messages'

interface Props {
    orderId: string
    onSave: RefreshEInvoiceDraft
    onCancel: () => void
}
export function EInvoiceDraftEditor(props: Props) {
    const query = useEInvoiceDraftEditor(props.orderId)
    if (query.isPending)
        return (
            <p role="status" className="p-6">
                {m.common_loading()}
            </p>
        )
    if (query.isError || !query.data)
        return (
            <div className="space-y-3 p-6">
                <p role="alert">
                    {query.error?.message || m.einvoice_draft_load_error()}
                </p>
                <Button variant="outline" onClick={() => void query.refetch()}>
                    {m.einvoice_viewer_retry()}
                </Button>
                <Button variant="ghost" onClick={props.onCancel}>
                    {m.einvoice_buyer_cancel()}
                </Button>
            </div>
        )
    return <DraftForm {...props} content={query.data} />
}
function DraftForm({
    orderId,
    content: snapshot,
    onSave,
    onCancel,
}: Props & { content: EInvoiceDraftContent }) {
    const inFlight = useRef(false)
    const workflow = useWorkflowTracking('einvoice_draft_edit', {
        autoStart: false,
        attemptKey: orderId,
        operation: 'update',
    })
    // Keep the revision and amounts the user opened even if another tab refreshes the query.
    const [content] = useState(snapshot)
    const buyer = content.invoice_buyer
    const form = useForm<EInvoiceDraftValues>({
        resolver: zodResolver(createEInvoiceDraftSchema()),
        defaultValues: {
            invoice_buyer: {
                buyer_type:
                    buyer.buyer_type === 'consumer' ? 'consumer' : 'company',
                buyer_name: buyer.buyer_name ?? '',
                tax_code: buyer.tax_code ?? '',
                address: buyer.address ?? '',
                email: buyer.email ?? '',
            },
            invoice_date: content.invoice_date,
            items: content.items
                .filter(editableDraftItem)
                .map(
                    ({
                        line_number,
                        item_name,
                        discount_percent,
                        vat_amount,
                    }) => ({
                        line_number,
                        item_name,
                        discount_percent,
                        vat_amount,
                        removed: false,
                    }),
                ),
        },
    })
    const [resetting, setResetting] = useState(false)
    const pending = form.formState.isSubmitting || resetting
    function showError(error: unknown) {
        form.setError('root', {
            message:
                (error instanceof ApiError &&
                    resolveApiErrorMessage(error.code, error.params)) ||
                (error instanceof Error
                    ? error.message
                    : m.einvoice_buyer_save_error()),
        })
    }
    async function resetFromOrder() {
        if (inFlight.current) return
        inFlight.current = true
        setResetting(true)
        form.clearErrors('root')
        try {
            await onSave(undefined, undefined, {
                reset_from_order: true,
                revision: content.revision,
            })
        } catch (error) {
            showError(error)
        } finally {
            inFlight.current = false
            setResetting(false)
        }
    }
    return (
        <FormProvider {...form}>
            <form
                noValidate
                className="flex min-h-0 flex-col overflow-hidden"
                onSubmit={form.handleSubmit(
                    async ({ invoice_buyer, invoice_date, items }) => {
                        if (inFlight.current) return
                        inFlight.current = true
                        form.clearErrors('root')
                        const removed = items
                            .filter((item) => item.removed)
                            .map((item) => item.line_number)
                        workflow.setContext({
                            removedLineCount: removed.length,
                            invoiceTotalDiffers: draftTotalDiffers(
                                content,
                                draftTotals(content, items).total,
                            ),
                        })
                        try {
                            await workflow.run(() =>
                                onSave(invoice_buyer, {
                                    revision: content.revision,
                                    invoice_date,
                                    items: items
                                        .filter((item) => !item.removed)
                                        .map(({ removed: _, ...item }) => item),
                                    // Old servers reject unknown fields, so only
                                    // send removals when there are some.
                                    ...(removed.length > 0 && {
                                        removed_line_numbers: removed,
                                    }),
                                }),
                            )
                        } catch (error) {
                            showError(error)
                        } finally {
                            inFlight.current = false
                        }
                    },
                )}
            >
                <div className="min-h-0 overflow-y-auto px-5 py-5 sm:px-6">
                    <fieldset disabled={pending} className="min-w-0 space-y-6">
                        {(snapshot.source_changed || snapshot.edited) && (
                            <EInvoiceDraftReset
                                pending={pending}
                                sourceChanged={snapshot.source_changed}
                                onReset={resetFromOrder}
                            />
                        )}
                        <EInvoiceBuyerFields />
                        <Field
                            name="invoice_date"
                            label={m.einvoice_draft_date()}
                            required
                        >
                            <div className="flex gap-2">
                                <Input
                                    id="invoice_date"
                                    type="date"
                                    className="max-w-56"
                                    {...form.register('invoice_date')}
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() =>
                                        form.setValue(
                                            'invoice_date',
                                            todayYMD(),
                                            {
                                                shouldDirty: true,
                                                shouldValidate: true,
                                            },
                                        )
                                    }
                                >
                                    {m.einvoice_draft_today()}
                                </Button>
                            </div>
                        </Field>
                        <EInvoiceDraftLines items={content.items} />
                        <EInvoiceDraftTotals content={content} />
                        {form.formState.errors.root && (
                            <p
                                role="alert"
                                className="text-sm text-destructive"
                            >
                                {form.formState.errors.root.message}
                            </p>
                        )}
                    </fieldset>
                </div>
                <div className="shrink-0 space-y-3 border-t bg-muted/30 px-5 py-4 sm:px-6">
                    <p className="text-sm text-muted-foreground">
                        {m.einvoice_buyer_save_hint()}
                    </p>
                    <div className="flex justify-end gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            className="h-11 px-4"
                            disabled={pending}
                            onClick={() => {
                                // Closes an attempt left open by a failed save.
                                workflow.finish('closed')
                                onCancel()
                            }}
                        >
                            {m.einvoice_buyer_cancel()}
                        </Button>
                        <Button
                            type="submit"
                            className="h-11 flex-1 px-4 sm:flex-none"
                            disabled={pending}
                        >
                            {pending
                                ? m.einvoice_buyer_saving()
                                : m.einvoice_buyer_save_preview()}
                        </Button>
                    </div>
                </div>
            </form>
        </FormProvider>
    )
}
